import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Save,
  Loader2,
  Briefcase,
  Dumbbell,
  Plus,
  X,
  AlertTriangle,
  Lock,
  Eye,
  EyeOff,
  FileText,
} from "lucide-react";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import TrainerVisibilityCard from "@/components/TrainerVisibilityCard";
import { useTranslation } from "react-i18next";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";

export default function UserProfileModule({ user, onUpdate }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "qr", "evaluations"]);
  const { getLocalizedBranch } = useLocalizedData();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    surname: "",
    phone: "",
    birth_date: "",
    gender: "",
  });
  const [careerStartDate, setCareerStartDate] = useState("");
  const [savingCareerDate, setSavingCareerDate] = useState(false);
  const [lookingForWork, setLookingForWork] = useState(false);
  const [savingWorkStatus, setSavingWorkStatus] = useState(false);
  
  // Trainer branch management
  const [knownBranches, setKnownBranches] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [savingBranches, setSavingBranches] = useState(false);
  const [showAddBranch, setShowAddBranch] = useState(false);
  
  // Trainer bio
  const [trainerBio, setTrainerBio] = useState("");
  const [savingBio, setSavingBio] = useState(false);

  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        surname: user.surname || "",
        phone: user.phone || "",
        birth_date: user.birth_date || "",
        gender: user.gender || "",
      });
      setLookingForWork(user.looking_for_work || false);
      setKnownBranches(user.known_branches || []);
      setCareerStartDate(user.career_start_date || "");
      setTrainerBio(user.bio || "");
    }
  }, [user]);

  // Fetch available branches for trainers
  useEffect(() => {
    const fetchBranches = async () => {
      if (user?.role !== "trainer") return;
      try {
        const response = await fetch(`${API}/public/branches`);
        if (response.ok) {
          const data = await response.json();
          const normalized = (Array.isArray(data) ? data : []).map(b => {
            if (typeof b === 'string') return { id: b, name: b, is_active: true };
            return b;
          }).filter(b => b.is_active !== false);
          setAvailableBranches(normalized);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, [user?.role]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profile),
      });

      if (response.ok) {
        toast.success(t('common:profileUpdated'));
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleWorkStatusChange = async (checked) => {
    setSavingWorkStatus(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ looking_for_work: checked }),
      });

      if (response.ok) {
        setLookingForWork(checked);
        toast.success(checked ? t("common:jobSearchMarked") : t("common:jobSearchRemoved"));
        if (onUpdate) onUpdate();
      } else {
        toast.error(t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingWorkStatus(false);
    }
  };

  // Save career start date for trainers
  const handleCareerDateChange = async (dateStr) => {
    // Immediately update local state
    setCareerStartDate(dateStr);
    
    // If empty, don't save
    if (!dateStr) return;
    
    setSavingCareerDate(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ career_start_date: dateStr }),
      });

      if (response.ok) {
        toast.success(t("common:toast_meslegeBaslamaTarihiKaydedildi"));
        // Don't call onUpdate() here - it causes a re-render that resets the state
        // The local state is already updated
      } else {
        toast.error(t('settings:updateFailed'));
        // Revert to original value on error
        setCareerStartDate(user?.career_start_date || "");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
      // Revert to original value on error
      setCareerStartDate(user?.career_start_date || "");
    } finally {
      setSavingCareerDate(false);
    }
  };
  
  // Save trainer bio
  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ bio: trainerBio }),
      });

      if (response.ok) {
        toast.success(t("common:toast_hakkimdaBilgisiKaydedildi"));
        if (onUpdate) onUpdate();
      } else {
        toast.error(t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingBio(false);
    }
  };

  // Add a new branch to trainer's known branches
  const handleAddBranch = async (branchName) => {
    if (knownBranches.includes(branchName)) {
      toast.error(t("common:toast_buBransZatenEkli"));
      return;
    }
    
    setSavingBranches(true);
    try {
      const newBranches = [...knownBranches, branchName];
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ known_branches: newBranches }),
      });

      if (response.ok) {
        setKnownBranches(newBranches);
        setShowAddBranch(false);
        toast.success(`${branchName} branşı eklendi`);
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:branchAddFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingBranches(false);
    }
  };

  // Remove a branch from trainer's known branches
  const handleRemoveBranch = async (branchName) => {
    if (knownBranches.length <= 1) {
      toast.error(t("common:toast_enAzBirBransinizOlmali"));
      return;
    }
    
    setSavingBranches(true);
    try {
      const newBranches = knownBranches.filter(b => b !== branchName);
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ known_branches: newBranches }),
      });

      if (response.ok) {
        setKnownBranches(newBranches);
        toast.success(`${branchName} branşı kaldırıldı`);
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:branchRemoveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingBranches(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordData;
    
    if (!current_password || !new_password || !confirm_password) {
      toast.error(t('common:allFieldsRequired'));
      return;
    }
    
    if (new_password !== confirm_password) {
      toast.error(t("common:toast_yeniSifrelerEslesmiyor"));
      return;
    }
    
    if (new_password.length < 6) {
      toast.error(t("common:toast_yeniSifreEnAz6"));
      return;
    }
    
    setSavingPassword(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(passwordData),
      });
      
      if (response.ok) {
        toast.success(t('common:passwordChangedSuccess'));
        setPasswordData({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setShowPasswordChange(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:passwordChangeFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingPassword(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: t("admin:admin"),
      business: t("common:businessOwner"),
      trainer: t("common:trainer"),
      member: t("common:member"),
    };
    return roles[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-600",
      business: "bg-blue-100 text-blue-600",
      trainer: "bg-green-100 text-green-600",
      member: "bg-purple-100 text-purple-600",
    };
    return colors[role] || "bg-grey-100 text-grey-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-heading font-bold text-xl text-navy">{t("trainers:myProfile")}</h2>
        <p className="text-grey text-sm">{t("common:kisiselBilgileriniziGoruntuleyinVeDuzenl")}</p>
      </div>

      {/* Profile Card */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-navy text-white text-xl">
                {profile.name?.charAt(0) || user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg">
                {profile.name} {profile.surname}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Mail className="w-3 h-3" />
                {user?.email}
              </CardDescription>
              <Badge className={`mt-2 ${getRoleBadgeColor(user?.role)}`}>
                {getRoleLabel(user?.role)}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                <User className="w-4 h-4 inline mr-2" />
                {t("trainers:firstNameLabel")}
              </Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder={t("common:ph_adiniz")}
                className="rounded-xl"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="surname">{t('common:surname')}</Label>
              <Input
                id="surname"
                value={profile.surname}
                onChange={(e) => setProfile({ ...profile, surname: e.target.value })}
                placeholder={t("common:ph_soyadiniz")}
                className="rounded-xl"
                data-testid="profile-surname-input"
              />
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">
              <Phone className="w-4 h-4 inline mr-2" />
              {t("trainers:phoneLabel")}
            </Label>
            <Input
              id="phone"
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              placeholder="05XX XXX XX XX"
              className="rounded-xl"
              data-testid="profile-phone-input"
            />
          </div>

          {/* Birth Date and Gender */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="birth_date">
                <Calendar className="w-4 h-4 inline mr-2" />
                {t('common:birthDate')}
              </Label>
              <DatePickerTR
                value={profile.birth_date ? new Date(profile.birth_date) : null}
                onChange={(date) => setProfile({ ...profile, birth_date: date ? toLocalDateString(date) : "" })}
                placeholder={t("common:ph_dogumTarihiSecin")}
                data-testid="profile-birthdate-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">{t('common:gender')}</Label>
              <Select
                value={profile.gender}
                onValueChange={(value) => setProfile({ ...profile, gender: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="profile-gender-select">
                  <SelectValue placeholder={t('common:pleaseSelect')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('common:male')}</SelectItem>
                  <SelectItem value="female">{t('common:female')}</SelectItem>
                  <SelectItem value="other">{t("evaluations:belirtmekİstemiyorum")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trainer Job Search - Only for trainers */}
          {user?.role === "trainer" && (
            <>
              {/* Career Start Date - {t("trainers:careerStartDate")} */}
              <div className="pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="career_start_date">
                    <Briefcase className="w-4 h-4 inline mr-2" />
                    {t("trainers:careerStartDate")}
                    <span className="text-xs text-grey ml-2">{t("trainers:forExperienceCalc")}</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <DatePickerTR
                      value={careerStartDate}
                      onChange={handleCareerDateChange}
                      placeholder={t("common:ph_meslegeBaslamaTarihiSecin")}
                      returnDateString={true}
                      fromYear={1970}
                      toYear={new Date().getFullYear()}
                      data-testid="profile-career-date-input"
                    />
                    {savingCareerDate && (
                      <Loader2 className="w-4 h-4 animate-spin text-navy" />
                    )}
                  </div>
                  {careerStartDate && (
                    <p className="text-xs text-grey">
                      Toplam deneyim: {(() => {
                        const start = new Date(careerStartDate);
                        const today = new Date();
                        const years = Math.floor((today - start) / (1000 * 60 * 60 * 24 * 365.25));
                        return `${years} yıl`;
                      })()}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Hakkımda / Bio Section */}
              <div className="pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-grey flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t("trainers:aboutMe")}
                  </Label>
                  <Textarea
                    placeholder={t("common:ph_kendiniziKisacaTanitin")}
                    value={trainerBio}
                    onChange={(e) => setTrainerBio(e.target.value)}
                    className="rounded-xl"
                    rows={3}
                  />
                  <Button
                    onClick={handleSaveBio}
                    disabled={savingBio}
                    className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                    data-testid="save-bio-btn"
                  >
                    {savingBio ? t("common:saving") : t("common:saveBio")}
                  </Button>
                </div>
              </div>

              {/* Known Branches Section */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-grey flex items-center gap-2">
                    <Dumbbell className="w-4 h-4" />
                    {t("trainers:myBranches")}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddBranch(!showAddBranch)}
                    disabled={savingBranches}
                    className="h-8 text-xs"
                    data-testid="add-branch-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {t("trainers:addBranch")}
                  </Button>
                </div>
                
                {/* Current Branches */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {knownBranches.length > 0 ? (
                    knownBranches.map((branch) => (
                      <Badge
                        key={branch}
                        className="bg-navy/10 text-navy hover:bg-navy/20 px-3 py-1.5 text-sm flex items-center gap-2"
                      >
                        {getLocalizedBranch(branch)}
                        <button
                          onClick={() => handleRemoveBranch(branch)}
                          disabled={savingBranches || knownBranches.length <= 1}
                          className={`ml-1 hover:text-red-500 transition-colors ${
                            knownBranches.length <= 1 ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                          title={knownBranches.length <= 1 ? t("common:mustHaveOneBranch") : t("common:removeBranch")}
                          data-testid={`remove-branch-${branch}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-grey">{t("common:henuzBransEklenmemis")}</p>
                  )}
                </div>
                
                {/* Add Branch Dropdown */}
                {showAddBranch && (
                  <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                    <p className="text-xs text-grey">Eklemek istediğiniz branşı seçin:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableBranches
                        .filter(b => !knownBranches.includes(b.name))
                        .map((branch) => (
                          <button
                            key={branch.id || branch.name}
                            onClick={() => handleAddBranch(branch.name)}
                            disabled={savingBranches}
                            className="px-3 py-1.5 rounded-full text-sm bg-white border hover:border-navy hover:text-navy transition-all disabled:opacity-50"
                            data-testid={`add-branch-option-${branch.name}`}
                          >
                            {getLocalizedBranch(branch.name)}
                          </button>
                        ))}
                      {availableBranches.filter(b => !knownBranches.includes(b.name)).length === 0 && (
                        <p className="text-sm text-grey">{t("common:tumBranslarZatenEkli")}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Warning about business usage */}
                <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 rounded-xl">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    {t("trainers:branchAssignedWarning")}
                  </p>
                </div>
              </div>

              {/* Career Status */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium text-grey mb-3">{t("trainers:careerStatus")}</h3>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">{t("evaluations:i̇sAriyorum")}</p>
                      <p className="text-sm text-grey">
                        {lookingForWork 
                          ? t("common:lookingForJobDesc") 
                          : t("qr:businessCanFindYou")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {savingWorkStatus && (
                      <Loader2 className="w-4 h-4 animate-spin text-grey" />
                    )}
                    <Switch
                      checked={lookingForWork}
                      onCheckedChange={handleWorkStatusChange}
                      disabled={savingWorkStatus}
                      data-testid="looking-for-work-switch"
                    />
                  </div>
                </div>
                {lookingForWork && (
                  <div className="mt-2 flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Briefcase className="w-3 h-3 mr-1" />
                      İş Arıyor
                    </Badge>
                    <span className="text-xs text-grey">{t("common:antrenorlerListesindeGorunuyorsunuz")}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Trainer Visibility Card - Ana Sayfa Görünürlüğü (KVKK) */}
          {user?.role === "trainer" && user?.user_id && (
            <div className="pt-4 border-t">
              <TrainerVisibilityCard 
                trainerId={user.user_id} 
                isOwnProfile={true} 
              />
            </div>
          )}

          {/* Account Info (Read-only) */}
          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium text-grey mb-3">{t("trainers:accountInfo")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-grey">
              <div>
                <span className="font-medium">Email:</span>{" "}
                <span className="text-navy">{user?.email}</span>
              </div>
              {user?.created_at && (
                <div>
                  <span className="font-medium">{t("trainers:registrationDate")}:</span>{" "}
                  <span className="text-navy">
                    {format(new Date(user.created_at), "d MMMM yyyy", { locale: getDateLocale() })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Password Change Section */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-navy flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {t("trainers:changePasswordLabel")}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordChange(!showPasswordChange)}
                className="rounded-xl"
              >
                {showPasswordChange ? t("common:cancel") : t("common:changePassword")}
              </Button>
            </div>
            
            {showPasswordChange && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                {/* Current Password */}
                <div className="space-y-2">
                  <Label>{t("common:currentPassword")}</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      placeholder={t("common:ph_mevcutSifreniziGirin")}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* New Password */}
                <div className="space-y-2">
                  <Label>{t("common:newPassword")}</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      placeholder={t("common:ph_yeniSifreniziGirinEnAz")}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-navy"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label>{t("trainers:newPasswordRepeat")}</Label>
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    placeholder={t("common:ph_yeniSifreniziTekrarGirin")}
                  />
                </div>
                
                {/* Change Button */}
                <Button
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                >
                  {savingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Değiştiriliyor...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      {t("trainers:changePasswordBtn")}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="profile-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t("trainers:saveProfile")}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
